import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

import type { ToolsTimeline } from '../../../@types/Company';
import type { FixedNewsletter, NewsletterType } from '../../../@types/Newsletter';
import { NEWSLETTER_THEME_TO_TYPE, NEWSLETTER_TYPE_LABELS } from '../../../@types/Newsletter';
import type { User } from '../../../@types/User';
import { useCompanyAttributionUsers } from '../../../hooks/useCompanyAttributionUsers';
import { setCompanyToolsTimeline } from '../../../store/AuthReducer';
import type { DashboardEventActivation, DashboardEventPositions, DashboardEventType, DashboardExactDates } from '../../../store/dashboardSlice';
import { loadTimelineData, resetTimelineToDefaults, togglePlanning, toggleTimelineLock, updateEventActivation, updateEventPosition, updateExactDate, updateNewsletterTheme, updatePersonAssignment } from '../../../store/dashboardSlice';
import type { RootState } from '../../../store/store';
import { getSalaries, sendTimelineInvitations, sendTimelineRowInvitation, updateCompanyToolsTimeline } from '../../../utils/api';
import { calendarYearForMonth, closestTuesdayForPosition, currentPlanningCycleYear, labelFromPositionHuman, positionFromExactDate } from '../../../utils/boitageTimeline';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { hasAnyModuleAccess } from '../../../utils/subscriptions';
import { extractUniqueUsersFromFonctionResults } from '../../../utils/users';
import { showToast } from '../../common/Notification';
import NewsletterPreviewPopup from './NewsletterPreviewPopup';
import { downloadBlankPlanningPdf, downloadPlanningPdf } from './PlanningPdf';

const TIMELINE_REFERENT_TAGS: Record<string, string> = {
  newsletter: '1',
  'referent-mails': '2',
  'ag-communication': '3',
  dpe: '4',
  'sci-mails': '5',
  boitage: '6',
  'property-communication': '7',
  'follow-up-meetings': '8',
};

type TimelineRowKey = keyof typeof TIMELINE_REFERENT_TAGS;

const AXE1_ROWS = new Set(['newsletter', 'referent-mails', 'ag-communication']);
const AXE2_ROWS = new Set(['dpe', 'sci-mails', 'boitage', 'property-communication']);

// Admin-only reference documents linked from the planning row number badges.
const TIMELINE_REFERENT_DOCS: Partial<Record<TimelineRowKey, string>> = {
  newsletter: '/downloads/planning-exemple-newsletter-1-dpe.pdf',
  'referent-mails': '/downloads/planning-modele-mail-referent.pdf',
  'ag-communication': '/downloads/planning-presentation-copropriete-syndic.pdf',
  dpe: '/downloads/planning-modele-mail-referent-traitement-dpe.pdf',
};

const renderReferentTag = (rowKey: TimelineRowKey, isAdmin = false) => {
  const tag = TIMELINE_REFERENT_TAGS[rowKey];
  if (!tag) {
    return null;
  }

  let colorClass = 'bg-rgpd-blue';
  if (AXE1_ROWS.has(rowKey)) {
    colorClass = 'bg-green-600';
  } else if (AXE2_ROWS.has(rowKey)) {
    colorClass = 'bg-violet-600';
  }

  const baseClass = `flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-black text-white shadow-md ${colorClass}`;

  const docUrl = TIMELINE_REFERENT_DOCS[rowKey];
  if (isAdmin && docUrl) {
    return (
      <a
        href={docUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Ouvrir le document de référence (admin)"
        className={`${baseClass} cursor-pointer no-underline ring-2 ring-amber-400 ring-offset-1 transition-transform hover:scale-110`}
      >
        {tag}
      </a>
    );
  }

  return (
    <div className={baseClass}>
      {tag}
    </div>
  );
};


const DEFAULT_START_MONTH = 9; // September
const DEFAULT_ROW_VISIBILITY: Record<string, boolean> = {
  'referent-mails': true,
  'sci-mails': true,
  'ag-communication': true,
  'property-communication': true,
  boitage: true,
};

interface PlanningModuleProps {
  restrictedToRowKeys?: string[];
}

function PlanningModule({ restrictedToRowKeys }: PlanningModuleProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { planningActivated, eventPositions, eventActivation, timelineLocked, personAssignments, exactDates } = useSelector((state: RootState) => state.dashboard);
  const { company, jwt, isAuthReady, isAdmin } = useSelector((state: RootState) => ({
    company: state.auth.company,
    jwt: state.auth.token,
    isAuthReady: state.auth.isAuthReady,
    isAdmin: state.auth.isAdmin,
  }));
  const [startMonth, setStartMonth] = useState(DEFAULT_START_MONTH);
  const [pendingStartMonth, setPendingStartMonth] = useState<number | null>(null);
  const [showStartMonthDialog, setShowStartMonthDialog] = useState(false);
  const [notifications, setNotifications] = useState<Array<{id: string, message: string}>>([]);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const { attributionUsers } = useCompanyAttributionUsers(company?.id ?? null);
  const [showDatePicker, setShowDatePicker] = useState<string | null>(null);
  const [openPersonSelect, setOpenPersonSelect] = useState<string | null>(null);
  const [isSendingInvitations, setIsSendingInvitations] = useState(false);
  const [sendingRowKey, setSendingRowKey] = useState<string | null>(null);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showNewsletterInfoModal, setShowNewsletterInfoModal] = useState(false);
  const [currentNewsletterEvent, setCurrentNewsletterEvent] = useState<string | null>(null);
  const [selectedNewsletterTheme, setSelectedNewsletterTheme] = useState<string | null>(null);
  const [showNewsletterPreview, setShowNewsletterPreview] = useState(false);
  const [previewNewsletterType, setPreviewNewsletterType] = useState<NewsletterType | null>(null);
  const [previewThemeLabel, setPreviewThemeLabel] = useState<string>('');

  const [isSavingAndLocking, setIsSavingAndLocking] = useState(false);
  const [isGeneratingBlankPdf, setIsGeneratingBlankPdf] = useState(false);
  const currentYear = useMemo(() => currentPlanningCycleYear(company?.toolsTimeline), [company?.toolsTimeline]);
  const [rowVisibility, setRowVisibility] = useState<Record<string, boolean>>(DEFAULT_ROW_VISIBILITY);

  const defaultEventPositions = useMemo<Pick<DashboardEventPositions, 'referent' | 'sci-referent' | 'boitage' | 'followup'>>(() => ({
    referent: { 'Référent 1': 1.5, 'Référent 2': 7.5, 'Référent 3': 11 },
    'sci-referent': { 'SCI 1': 0.5, 'SCI 2': 6.5, 'SCI 3': 10.5 },
    boitage: { 'Boîtage 1': 2.5, 'Boîtage 2': 8.5 },
    followup: { 'Point 1': 3, 'Point 2': 7 },
  }), []);

  const defaultEventActivation = useMemo<DashboardEventActivation>(() => ({
    referent: { 'Référent 1': true, 'Référent 2': true, 'Référent 3': false },
    'sci-referent': { 'SCI 1': true, 'SCI 2': true, 'SCI 3': false },
    boitage: { 'Boîtage 1': true, 'Boîtage 2': true },
  }), []);

  const safeEventPositions = useMemo<DashboardEventPositions>(() => ({
    referent: { ...defaultEventPositions.referent, ...(eventPositions.referent ?? {}) },
    'sci-referent': { ...defaultEventPositions['sci-referent'], ...(eventPositions['sci-referent'] ?? {}) },
    boitage: { ...defaultEventPositions.boitage, ...(eventPositions.boitage ?? {}) },
    newsletter: eventPositions.newsletter,
    followup: { ...defaultEventPositions.followup, ...(eventPositions.followup ?? {}) },
  }), [defaultEventPositions, eventPositions]);

  const safeEventActivation = useMemo<DashboardEventActivation>(() => ({
    referent: { ...defaultEventActivation.referent, ...(eventActivation?.referent ?? {}) },
    'sci-referent': { ...defaultEventActivation['sci-referent'], ...(eventActivation?.['sci-referent'] ?? {}) },
    boitage: { ...defaultEventActivation.boitage, ...(eventActivation?.boitage ?? {}) },
  }), [defaultEventActivation, eventActivation]);

  const isCompanyLoading = !company?.id && !isAuthReady;

  const followUpAttendees = useMemo(() => {
    const attendeeIds = new Set<number>();
    for (const userId of Object.values(personAssignments)) {
      if (userId) {
        attendeeIds.add(userId);
      }
    }
    const legalRep = companyUsers.find((u) =>
      u.fonctions?.some((f) => f.type === 2 && f.company?.id === company?.id),
    );
    if (legalRep) {
      attendeeIds.add(legalRep.id);
    }
    return companyUsers.filter((u) => attendeeIds.has(u.id));
  }, [personAssignments, companyUsers, company?.id]);

  const MOIS_NOMS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const MOIS_COURTS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  // Measure month header height to correctly place the today line below it
  const monthHeaderRef = useRef<HTMLDivElement | null>(null);
  const [todayLineTop, setTodayLineTop] = useState<number>(0);

  useEffect(() => {
    const updateTodayLineTop = () => {
      const el = monthHeaderRef.current;
      if (el) {
        setTodayLineTop(el.offsetTop + el.offsetHeight);
      }
    };
    updateTodayLineTop();
    window.addEventListener('resize', updateTodayLineTop);
    return () => window.removeEventListener('resize', updateTodayLineTop);
  }, [startMonth]);

  // Helper function centralized in utils
  const getDateFromPosition = (position: number): string => labelFromPositionHuman(startMonth, position);

  const handleTogglePlanning = () => {
    dispatch(togglePlanning());
  };

  // Build the set of exactDates entries that are missing for any planning event
  // (referent / sci-referent / boitage activated events, every newsletter, every follow-up point).
  // Each missing entry is filled with the closest Tuesday in its current period.
  const buildMissingClosestTuesdays = (): DashboardExactDates => {
    const additions: DashboardExactDates = {};
    const sectionedTypes: Array<'referent' | 'sci-referent' | 'boitage'> = ['referent', 'sci-referent', 'boitage'];
    for (const type of sectionedTypes) {
      const positions = safeEventPositions[type] ?? {};
      const activations = safeEventActivation[type] ?? {};
      for (const [eventName, position] of Object.entries(positions)) {
        if (!activations[eventName]) {
          continue;
        }
        const key = `${type}-${eventName}`;
        if (exactDates[key]) {
          continue;
        }
        additions[key] = closestTuesdayForPosition(startMonth, position, currentYear);
      }
    }
    if (safeEventPositions.newsletter) {
      for (const [eventName, val] of Object.entries(safeEventPositions.newsletter)) {
        const key = `newsletter-${eventName}`;
        if (exactDates[key]) {
          continue;
        }
        additions[key] = closestTuesdayForPosition(startMonth, val.position, currentYear);
      }
    }
    if (safeEventPositions.followup) {
      for (const [eventName, position] of Object.entries(safeEventPositions.followup)) {
        const key = `followup-${eventName}`;
        if (exactDates[key]) {
          continue;
        }
        additions[key] = closestTuesdayForPosition(startMonth, position, currentYear);
      }
    }
    return additions;
  };

  const handleToggleTimelineLock = () => {
    dispatch(toggleTimelineLock());
  };

  const handleApplyAutoDates = () => {
    const additions = buildMissingClosestTuesdays();
    const addedCount = Object.keys(additions).length;
    if (addedCount === 0) {
      showToast('info', 'Toutes les dates sont déjà définies.');
      return;
    }
    dispatch(loadTimelineData({ exactDates: additions }));
    showToast('success', `${addedCount} date(s) initialisée(s) au mardi le plus proche.`);
  };

  const missingExactDatesCount = useMemo<number>(() => {
    let count = 0;
    const sectionedTypes: Array<'referent' | 'sci-referent' | 'boitage'> = ['referent', 'sci-referent', 'boitage'];
    for (const type of sectionedTypes) {
      const positions = safeEventPositions[type] ?? {};
      const activations = safeEventActivation[type] ?? {};
      for (const eventName of Object.keys(positions)) {
        if (!activations[eventName]) {
          continue;
        }
        if (!exactDates[`${type}-${eventName}`]) {
          count += 1;
        }
      }
    }
    if (safeEventPositions.newsletter) {
      for (const eventName of Object.keys(safeEventPositions.newsletter)) {
        if (!exactDates[`newsletter-${eventName}`]) {
          count += 1;
        }
      }
    }
    if (safeEventPositions.followup) {
      for (const eventName of Object.keys(safeEventPositions.followup)) {
        if (!exactDates[`followup-${eventName}`]) {
          count += 1;
        }
      }
    }
    return count;
  }, [safeEventPositions, safeEventActivation, exactDates]);

  const handleSaveAndLock = async() => {
    if (!company?.id || !jwt || isSavingAndLocking) {
      return;
    }

    setIsSavingAndLocking(true);
    
    try {
      // Save timeline data
      const timelineData: { toolsTimeline: ToolsTimeline } = {
        toolsTimeline: {
          ...(company.toolsTimeline ?? {}),
          [currentYear]: {
            startMonth,
            eventPositions,
            eventActivation,
            rowVisibility,
            personAssignments,
            exactDates,
          },
        },
      };

      const response = await fetch(updateCompanyToolsTimeline(company.id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify(timelineData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save timeline');
      }

      // Update Redux store with the saved toolsTimeline only, to avoid resetting other flags
      const updatedCompany = await response.json();
      if (updatedCompany && updatedCompany.toolsTimeline) {
        dispatch(setCompanyToolsTimeline(updatedCompany.toolsTimeline));
      }
      
      // Lock the timeline after successful save
      dispatch(toggleTimelineLock());
      
      showToast('success', 'Planning sauvegardé et verrouillé');

      try {
        await downloadPlanningPdf({
          companyName: company.name ?? '',
          year: currentYear,
          startMonth,
          eventPositions,
          eventActivation,
          personAssignments,
          exactDates,
          rowVisibility,
          companyUsers,
          referentSignataires: attributionUsers.referentSignataires,
          dpeQualificationManagers: attributionUsers.dpeQualificationManagers,
          agDocumentManagers: attributionUsers.agDocumentManagers,
        });
      } catch (pdfError) {
        console.error('Error generating planning PDF:', pdfError);
      }
      
    } catch (error) {
      console.error('Error saving timeline:', error);
      const message = error instanceof Error && error.message ? error.message : 'Erreur lors de la sauvegarde du planning';
      showToast('error', message);
    } finally {
      setIsSavingAndLocking(false);
    }
  };

  const handleDownloadBlankPdf = async() => {
    setIsGeneratingBlankPdf(true);
    try {
      await downloadBlankPlanningPdf({
        year: currentYear,
        startMonth,
        eventPositions: safeEventPositions,
        eventActivation: safeEventActivation,
        rowVisibility,
      });
      showToast('success', 'Planning vierge téléchargé');
    } catch (error) {
      console.error('Error generating blank planning PDF:', error);
      showToast('error', 'Erreur lors de la génération du planning vierge');
    } finally {
      setIsGeneratingBlankPdf(false);
    }
  };

  const handleSendAllInvitations = async() => {
    if (!company?.id || !jwt || isSendingInvitations) {
      return;
    }
    setIsSendingInvitations(true);
    try {
      const response = await fetch(sendTimelineInvitations(company.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        showToast('error', errorData?.message || 'Erreur serveur lors de l\'envoi des invitations.');
        return;
      }
      const data = await response.json();
      if (data.sent > 0) {
        showToast('success', `${data.sent} invitation(s) envoyée(s) avec succès.`);
      } else if (!data.errors?.length) {
        showToast('warning', 'Aucune invitation à envoyer. Vérifiez les attributions et dates.');
      }
      if (data.errors?.length > 0) {
        data.errors.forEach((err: string) => showToast('error', err));
      }
    } catch {
      showToast('error', 'Erreur lors de l\'envoi des invitations.');
    } finally {
      setIsSendingInvitations(false);
    }
  };

  const handleSendRowInvitation = async(rowKey: string) => {
    if (!company?.id || !jwt || sendingRowKey) {
      return;
    }
    setSendingRowKey(rowKey);
    try {
      const response = await fetch(sendTimelineRowInvitation(company.id, rowKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        showToast('error', errorData?.message || 'Erreur serveur lors de l\'envoi.');
        return;
      }
      const data = await response.json();
      if (data.sent > 0) {
        showToast('success', `${data.sent} invitation(s) envoyée(s).`);
      } else if (!data.errors?.length) {
        showToast('warning', 'Aucune invitation à envoyer pour cette ligne.');
      }
      if (data.errors?.length > 0) {
        data.errors.forEach((err: string) => showToast('error', err));
      }
    } catch {
      showToast('error', 'Erreur lors de l\'envoi.');
    } finally {
      setSendingRowKey(null);
    }
  };

  const hasAnyInvitableEvents = useMemo(() => {
    const periodRows = ['newsletter', 'referent-mails', 'sci-mails', 'boitage'];
    const hasStandardInvitable = periodRows.some((rowKey) => {
      const userId = personAssignments[rowKey];
      if (!userId) {
        return false;
      }
      return Object.keys(exactDates).some((key) => key.startsWith(
        rowKey === 'referent-mails' ? 'referent-' : rowKey === 'sci-mails' ? 'sci-referent-' : `${rowKey}-`,
      ));
    });
    const hasFollowUpInvitable = followUpAttendees.length > 0
      && Object.keys(exactDates).some((key) => key.startsWith('followup-'));
    return hasStandardInvitable || hasFollowUpInvitable;
  }, [personAssignments, exactDates, followUpAttendees]);

  const updateCalendar = () => {
    // This will trigger re-render with new positions
  };

  // No autosave: only save when clicking the Sauvegarder (save and lock) button

  // Initialize timeline data from saved company data.
  // Always reset first so that switching company clears stale planning data
  // from the previously selected company before loading the new one.
  useEffect(() => {
    if (!company?.id) {
      return;
    }

    dispatch(resetTimelineToDefaults());

    const savedTimelineData = company.toolsTimeline?.[String(currentYear)];

    if (!savedTimelineData) {
      setStartMonth(DEFAULT_START_MONTH);
      setRowVisibility(DEFAULT_ROW_VISIBILITY);
      return;
    }

    setStartMonth(typeof savedTimelineData.startMonth === 'number' ? savedTimelineData.startMonth : DEFAULT_START_MONTH);

    if (savedTimelineData.eventPositions || savedTimelineData.eventActivation || savedTimelineData.personAssignments || savedTimelineData.exactDates) {
      dispatch(loadTimelineData({
        eventPositions: savedTimelineData.eventPositions,
        eventActivation: savedTimelineData.eventActivation,
        personAssignments: savedTimelineData.personAssignments,
        exactDates: savedTimelineData.exactDates,
      }));
    }

    if (savedTimelineData.rowVisibility && typeof savedTimelineData.rowVisibility === 'object') {
      setRowVisibility({ ...DEFAULT_ROW_VISIBILITY, ...savedTimelineData.rowVisibility });
    } else {
      setRowVisibility(DEFAULT_ROW_VISIBILITY);
    }
  }, [company?.id, dispatch, currentYear]);

  // Load employees for person assignment
  useEffect(() => {
    if (company?.id && jwt) {
      fetch(getSalaries(company.id, true), JWTHeader(jwt))
        .then((response) => response.json())
        .then((data) => {
          if (data?.results) {
            setCompanyUsers(
              extractUniqueUsersFromFonctionResults(
                data.results,
                (fonctionResult) => fonctionResult.company?.id === company.id,
              ),
            );
          }
        })
        .catch(() => {});
    }
  }, [company?.id, jwt]);

  const userOptions = useMemo(() => {
    const sorted = [...companyUsers].sort((a, b) => a.lastname.localeCompare(b.lastname));
    return [
      { value: null as number | null, label: 'Non attribué' },
      ...sorted.map((user) => ({
        value: user.id as number | null,
        label: `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`,
      })),
    ];
  }, [companyUsers]);

  const getUserLabel = useCallback((userId: number | null | undefined): string => {
    if (!userId) {
      return '';
    }
    const user = companyUsers.find((u) => u.id === userId);
    if (!user) {
      return '';
    }
    return `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`;
  }, [companyUsers]);

  const getUserOptionValue = useCallback((userId: number | null | undefined) => {
    if (!userId) {
      return { value: null as number | null, label: 'Non attribué' };
    }
    const label = getUserLabel(userId);
    if (label) {
      return { value: userId as number | null, label };
    }
    return { value: null as number | null, label: 'Non attribué' };
  }, [getUserLabel]);

  // Initialize calendar when planning is activated
  useEffect(() => {
    if (planningActivated) {
      updateCalendar();
    }
  }, [planningActivated, startMonth]);

  const showDateNotification = (message: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message }]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 2000);
  };

  const moveEvent = (eventType: 'referent' | 'sci-referent' | 'boitage' | 'followup' | 'newsletter', eventText: string, direction: number) => {
    let currentPosition: number;
    if (eventType === 'newsletter') {
      // specific case for ther first newsletter, they MUST send it first off
      currentPosition = safeEventPositions.newsletter?.[eventText]?.position ?? 0;
    } else {
      const posMap = eventType === 'followup' ? safeEventPositions.followup : safeEventPositions[eventType];
      currentPosition = (posMap?.[eventText]) || 0;
    }
    const newPosition = Math.max(0, Math.min(11.5, currentPosition + direction));

    dispatch(updateEventPosition({ type: eventType, eventText, position: newPosition }));

    const eventKey = `${eventType}-${eventText}`;
    const newDate = closestTuesdayForPosition(startMonth, newPosition, currentYear);
    dispatch(updateExactDate({ eventKey, date: newDate }));

    showDateNotification(`Repositionné au ${newDate.day} ${MOIS_NOMS[newDate.month - 1].toLowerCase()} (${getDateFromPosition(newPosition)})`);
  };

  // Resolve the event type / name / current position for a given exactDates key
  // (e.g. "sci-referent-SCI 1", "newsletter-Newsletter 1").
  const resolveEventForExactDateKey = (eventKey: string): { type: DashboardEventType; eventText: string; position: number } | null => {
    const prefixes: Array<{ prefix: string; type: DashboardEventType }> = [
      { prefix: 'sci-referent-', type: 'sci-referent' },
      { prefix: 'referent-', type: 'referent' },
      { prefix: 'boitage-', type: 'boitage' },
      { prefix: 'newsletter-', type: 'newsletter' },
      { prefix: 'followup-', type: 'followup' },
    ];
    const match = prefixes.find((p) => eventKey.startsWith(p.prefix));
    if (!match) {
      return null;
    }
    const eventText = eventKey.slice(match.prefix.length);

    let position: number | undefined;
    if (match.type === 'newsletter') {
      position = safeEventPositions.newsletter?.[eventText]?.position;
    } else if (match.type === 'followup') {
      position = safeEventPositions.followup?.[eventText];
    } else {
      position = safeEventPositions[match.type]?.[eventText];
    }

    if (typeof position !== 'number') {
      return null;
    }

    return { type: match.type, eventText, position };
  };

  const closeStartMonthDialog = () => {
    setShowStartMonthDialog(false);
    setPendingStartMonth(null);
  };

  // Option A: keep the user-set exact dates and move events so each one lands on its date.
  const applyKeepDatesForStartMonth = (newMonth: number) => {
    const nextPositions: DashboardEventPositions = {
      referent: { ...safeEventPositions.referent },
      'sci-referent': { ...safeEventPositions['sci-referent'] },
      boitage: { ...safeEventPositions.boitage },
      newsletter: safeEventPositions.newsletter ? { ...safeEventPositions.newsletter } : undefined,
      followup: safeEventPositions.followup ? { ...safeEventPositions.followup } : undefined,
    };
    let updated = 0;

    for (const [eventKey, date] of Object.entries(exactDates)) {
      if (!date || typeof date.day !== 'number' || typeof date.month !== 'number') {
        continue;
      }
      const event = resolveEventForExactDateKey(eventKey);
      if (!event) {
        continue;
      }
      const newPosition = positionFromExactDate(newMonth, date.day, date.month);
      if (event.type === 'newsletter') {
        const existing = nextPositions.newsletter?.[event.eventText];
        if (existing && nextPositions.newsletter) {
          nextPositions.newsletter[event.eventText] = { ...existing, position: newPosition };
          updated += 1;
        }
      } else if (event.type === 'followup') {
        if (nextPositions.followup) {
          nextPositions.followup[event.eventText] = newPosition;
          updated += 1;
        }
      } else {
        nextPositions[event.type][event.eventText] = newPosition;
        updated += 1;
      }
    }

    dispatch(loadTimelineData({ eventPositions: nextPositions }));
    setStartMonth(newMonth);
    closeStartMonthDialog();
    if (updated > 0) {
      showToast('success', `Mois de lancement mis à jour : ${updated} événement(s) repositionné(s) sur leurs dates exactes.`);
    } else {
      showToast('success', 'Mois de lancement mis à jour.');
    }
  };

  // Option B: keep the existing event order and replace each exact date with the closest Tuesday
  // in the new period.
  const applyClosestTuesdayForStartMonth = (newMonth: number) => {
    const nextExactDates: DashboardExactDates = {};
    let updated = 0;

    for (const [eventKey, date] of Object.entries(exactDates)) {
      if (!date || typeof date.day !== 'number' || typeof date.month !== 'number') {
        continue;
      }
      const event = resolveEventForExactDateKey(eventKey);
      if (!event) {
        nextExactDates[eventKey] = date;
        continue;
      }
      nextExactDates[eventKey] = closestTuesdayForPosition(newMonth, event.position, currentYear);
      updated += 1;
    }

    dispatch(loadTimelineData({ exactDates: nextExactDates }));
    setStartMonth(newMonth);
    closeStartMonthDialog();
    if (updated > 0) {
      showToast('success', `Mois de lancement mis à jour : ${updated} date(s) recalculée(s) au mardi le plus proche.`);
    } else {
      showToast('success', 'Mois de lancement mis à jour.');
    }
  };

  const handleStartMonthSelect = (newMonth: number) => {
    if (newMonth === startMonth) {
      return;
    }
    const hasAnyExactDate = Object.values(exactDates).some(
      (d) => d && typeof d.day === 'number' && typeof d.month === 'number',
    );
    if (!hasAnyExactDate) {
      setStartMonth(newMonth);
      return;
    }
    setPendingStartMonth(newMonth);
    setShowStartMonthDialog(true);
  };

  // Removed autosave: editing the planning does not trigger backend requests automatically





  const handleEventClick = (type: 'newsletter' | 'referent' | 'sci-referent' | 'boitage', theme?: string) => {
    if (timelineLocked) {
      // When locked, clicking should open the appropriate action/route
      if (type === 'newsletter') {
        if (theme) {
          const mappedType = NEWSLETTER_THEME_TO_TYPE[theme];

          if (mappedType) {
            setPreviewNewsletterType(mappedType);
            setPreviewThemeLabel(theme ?? NEWSLETTER_TYPE_LABELS[mappedType] ?? '');
            setShowNewsletterPreview(true);
            return;
          }
        }

        navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
      } else if (type === 'referent') {
        // Navigate to Referent module
        navigate(ROUTES.COMMUNICATION_REFERENT_FORMATION);
      } else if (type === 'sci-referent') {
        // Redirect to SCI page
        navigate(ROUTES.MONITORING_SCIS_OVERALL);
      } else if (type === 'boitage') {
        navigate(ROUTES.PROSPECTING_BOITAGE_QUALIFICATION);
      }
    }
    // When unlocked, the event is editable (move buttons are visible and clickable)
  };



  // Newsletter theme content data
  const newsletterThemeData = {
    'DPE': {
      title: 'Newsletter DPE Collectif',
      emoji: `<span role="img" aria-label="chart">📊</span>`,
      objective: 'Sensibiliser sur l\'importance du DPE et positionner votre référent',
      content: [
        'Explication des nouveaux DPE collectifs et leur impact',
        'Conseils pour améliorer la performance énergétique',
        'Mise en avant de votre référent mutation/location',
        'Incitation à contacter le référent pour projets de vente/location',
      ],
      interest: 'Cette première newsletter établit votre expertise et crée le lien avec votre référent pour tous futurs projets immobiliers des copropriétaires.',
    },
    'Vente': {
      title: 'Newsletter Vente',
      emoji: `<span role="img" aria-label="briefcase">💼</span>`,
      objective: 'Positionner votre expertise en transaction et génération de leads vente',
      content: [
        'Conseils pour optimiser la valorisation des biens',
        'Tendances du marché immobilier local',
        'Services d\'estimation gratuite et accompagnement vente',
        'Mise en avant de vos succès et témoignages clients',
      ],
      interest: 'Cette newsletter vous positionne comme l\'expert incontournable en vente immobilière auprès de vos copropriétaires.',
    },
    'Travaux': {
      title: 'Newsletter Travaux',
      emoji: `<span role="img" aria-label="wrench">🔧</span>`,
      objective: 'Accompagner vos copropriétaires dans leurs projets de rénovation',
      content: [
        'Travaux d\'amélioration énergétique et aides disponibles',
        'Rénovations qui valorisent le bien pour la vente/location',
        'Partenaires de confiance et artisans recommandés',
        'Impact des travaux sur la performance DPE',
      ],
      interest: 'Cette newsletter vous positionne comme conseil global et génère des opportunités via vos partenaires artisans.',
    },
    'Vivre ensemble': {
      title: 'Newsletter Vivre ensemble',
      emoji: `<span role="img" aria-label="community">🏘️</span>`,
      objective: 'Favoriser le bien-vivre en copropriété et renforcer le lien communautaire',
      content: [
        'Règles et bonnes pratiques du vivre ensemble en copropriété',
        'Gestion des conflits et médiation entre voisins',
        'Évolutions réglementaires et obligations en copropriété',
        'Conseils pour une copropriété harmonieuse et bien gérée',
      ],
      interest: 'Cette newsletter renforce votre image de partenaire de confiance et favorise un environnement propice aux projets immobiliers.',
    },
    'Location': {
      title: 'Newsletter Location',
      emoji: `<span role="img" aria-label="key">🔑</span>`,
      objective: 'Accompagner vos copropriétaires dans leurs projets de location',
      content: [
        'Réglementation et obligations du propriétaire bailleur',
        'Conseils pour optimiser la mise en location de son bien',
        'Gestion locative et sécurisation des revenus',
        'Tendances du marché locatif et opportunités',
      ],
      interest: 'Cette newsletter vous positionne comme l\'expert en gestion locative et génère des mandats de location.',
    },
  };

  const handleNewsletterThemeChange = (eventName: string, newTheme: string) => {
    dispatch(updateNewsletterTheme({ eventText: eventName, theme: newTheme }));
  };

  // Handle newsletter click when unlocked - open theme selection modal
  const handleNewsletterClick = (eventName: string) => {
    if (!timelineLocked) {
      setCurrentNewsletterEvent(eventName);
      setShowThemeModal(true);
    }
  };

  // Handle theme selection - close theme modal and open info modal
  const handleThemeSelection = (theme: string) => {
    setShowThemeModal(false);
    if (currentNewsletterEvent) {
      // Update theme in Redux
      handleNewsletterThemeChange(currentNewsletterEvent, theme);
      // Show info modal for selected theme
      setSelectedNewsletterTheme(theme);
      setShowNewsletterInfoModal(true);
    }
  };

  // Close modals
  const closeThemeModal = () => {
    setShowThemeModal(false);
    setCurrentNewsletterEvent(null);
  };

  const closeNewsletterInfoModal = () => {
    setShowNewsletterInfoModal(false);
    setSelectedNewsletterTheme(null);
    setCurrentNewsletterEvent(null);
  };

  const closeNewsletterPreview = () => {
    setShowNewsletterPreview(false);
    setPreviewNewsletterType(null);
    setPreviewThemeLabel('');
  };

  const handleOpenNewsletterPreview = (newsletter: FixedNewsletter, templateName: string) => {
    closeNewsletterPreview();
    const newsletterId = `fixed:${newsletter.date}:${newsletter.slug}:${templateName}`;
    navigate(ROUTES.COMMUNICATION_NEWSLETTER_PREVIEW.replace(':id', newsletterId));
  };

  const handleOpenNewsletterList = () => {
    closeNewsletterPreview();
    navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
  };

  const DAYS_IN_MONTH: Record<number, number> = { 1: 31, 2: 28, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31 };

  const getMonthFromPosition = (position: number): number => {
    const monthIndexFromStart = Math.floor(position);
    return ((startMonth - 1 + monthIndexFromStart) % 12) + 1;
  };

  const getExactDateKey = (type: string, eventName: string): string => `${type}-${eventName}`;

  const formatExactDate = (date: { day: number; month: number }): string => {
    return `${date.day} ${MOIS_COURTS[date.month - 1]}`;
  };

  const handleExactDateChange = (eventKey: string, day: number, month: number) => {
    if (month === 2 && day === 29) {
      showToast('warning', 'Le 29 février n\'est pas autorisé.');
      return;
    }
    dispatch(updateExactDate({ eventKey, date: { day, month } }));
  };

  const renderExactDateBadge = (type: string, eventName: string) => {
    const key = getExactDateKey(type, eventName);
    const date = exactDates[key];
    if (!date) {
      return null;
    }
    return (
      <div className="mt-0.5 text-[0.55rem] font-semibold text-white/90">
        {formatExactDate(date)}
      </div>
    );
  };

  const getDateInputBounds = (position: number): { min: string; max: string } => {
    const centerMonth = getMonthFromPosition(position);
    const nextMonth = centerMonth === 12 ? 1 : centerMonth + 1;

    const minYear = calendarYearForMonth(centerMonth, startMonth, currentYear);
    const maxYear = nextMonth < centerMonth ? minYear + 1 : minYear;

    const minStr = `${minYear}-${String(centerMonth).padStart(2, '0')}-01`;
    const lastDay = DAYS_IN_MONTH[nextMonth] ?? 31;
    const maxStr = `${maxYear}-${String(nextMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    return { min: minStr, max: maxStr };
  };

  const exactDateToInputValue = (date: { day: number; month: number } | undefined): string => {
    if (!date) {
      return '';
    }
    const year = calendarYearForMonth(date.month, startMonth, currentYear);
    return `${year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  };

  const renderDatePickerButton = (type: string, eventName: string, position: number) => {
    const eventKey = getExactDateKey(type, eventName);
    const currentDate = exactDates[eventKey];
    const isOpen = showDatePicker === eventKey;
    const { min, max } = getDateInputBounds(position);

    return (
      <div className="relative">
        <button
          className="flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-full border-none bg-white/20 text-[0.65rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/30"
          title={currentDate ? formatExactDate(currentDate) : 'Définir la date exacte'}
          onClick={(e) => {
            e.stopPropagation();
            setShowDatePicker(isOpen ? null : eventKey);
          }}
        >
          <span role="img" aria-label="calendar">📅</span>
        </button>
        {isOpen && (
          <div
            className="absolute top-full right-0 z-[100] mt-1 min-w-[200px] rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-xs font-semibold text-gray-700">Date exacte</div>
            <input
              type="date"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-800 focus:border-rgpd-blue focus:outline-none"
              value={exactDateToInputValue(currentDate)}
              min={min}
              max={max}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  return;
                }
                const [, m, d] = val.split('-').map(Number);
                if (m && d) {
                  handleExactDateChange(eventKey, d, m);
                }
              }}
            />
            {currentDate && (
              <button
                className="mt-2 w-full rounded bg-red-50 px-2 py-1 text-[0.6rem] text-red-600 hover:bg-red-100"
                onClick={() => dispatch(updateExactDate({ eventKey, date: null }))}
              >
                Supprimer la date
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderNewsletterEvent = (eventName: string, position: number, theme: string, isEditable: boolean = true) => {
    const leftPosition = (position / 12) * 100;
    
    return (
      <div
        key={`newsletter-${eventName}`}
        className={`group !absolute !top-1/2 z-20 inline-flex min-w-16 !-translate-y-1/2 !cursor-pointer items-center justify-between gap-2 rounded-md bg-rgpd-blue px-3 py-2 text-center text-xs font-medium text-white shadow-lg transition-all duration-300 hover:shadow-xl ${
          timelineLocked ? 'cursor-pointer' : isEditable ? 'cursor-pointer ring-2 ring-transparent hover:bg-rgpd-blue-dark hover:ring-white/30' : ''
        }`}
        style={{ left: `${leftPosition}%` }}
        onClick={() => {
          if (timelineLocked) {
            handleEventClick('newsletter', theme);
          } else if (isEditable) {
            handleNewsletterClick(eventName);
          }
        }}
      >

        {/* Move buttons for editable newsletters (not DPE) - only when unlocked */}
        {!timelineLocked && isEditable && (
          <div className="z-10 flex items-center gap-1">
            <button
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm border-none bg-white/20 text-[0.6rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/40"
              onClick={(e) => {
                e.stopPropagation();
                moveEvent('newsletter', eventName, -0.5);
              }}
            >
              ◀
            </button>
            <button
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm border-none bg-white/20 text-[0.6rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/40"
              onClick={(e) => {
                e.stopPropagation();
                moveEvent('newsletter', eventName, 0.5);
              }}
            >
              ▶
            </button>
          </div>
        )}
        
        <div className="text-center whitespace-nowrap">
          {theme}
          {renderExactDateBadge('newsletter', eventName)}
        </div>
        
        <div className="flex items-center gap-1">
          {!timelineLocked && renderDatePickerButton('newsletter', eventName, position)}
          <button
            className="group/tooltip relative flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-full border-none bg-white/20 text-[0.65rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/30"
            title={getDateFromPosition(position)}
            onClick={(e) => {
              e.stopPropagation();
              if (timelineLocked) {
                navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
              }
            }}
          >
            <span role="img" aria-label="information">ℹ️</span>
            
            <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 transform rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover/tooltip:opacity-100">
              {getDateFromPosition(position)}
              <div className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 transform border-t-2 border-r-2 border-l-2 border-transparent border-t-gray-800"></div>
            </div>
          </button>
        </div>
      </div>
    );
  };

  const renderEvent = (
    text: string,
    type: 'newsletter' | 'referent' | 'sci-referent' | 'boitage',
    position: number,
    isActive: boolean,
    onToggle?: (next: boolean) => void,
    disableToggle?: boolean,
  ) => {
    const leftPosition = (position / 12) * 100;
    
    let bgColorClass = 'bg-green-600';
    if (type === 'newsletter' || type === 'referent') {
      bgColorClass = 'bg-green-600';
    } else if (type === 'sci-referent' || type === 'boitage') {
      bgColorClass = 'bg-violet-600';
    }
    
    const isToggleable = Boolean(onToggle) && !timelineLocked;
    const zIndexClass = isActive ? 'z-30' : 'z-10';
    const interactionClass = timelineLocked ? 'cursor-pointer' : isActive ? 'cursor-pointer' : 'cursor-not-allowed opacity-60';
    const baseClasses = `!absolute !top-1/2 !-translate-y-1/2 ${bgColorClass} text-white text-xs font-medium px-3 py-2 rounded-md min-w-16 text-center shadow-lg transition-all duration-300 hover:shadow-xl inline-flex items-center justify-between gap-2 group`;

    return (
      <div
        key={`${type}-${text}`}
        className={`${baseClasses} ${zIndexClass} ${interactionClass}`}
        style={{ left: `${leftPosition}%` }}
        onClick={() => isActive && handleEventClick(type as 'referent' | 'sci-referent' | 'newsletter' | 'boitage')}
      >
        {/* Toggle switch */}
        {isToggleable && (
          <button
            type="button"
            className={`absolute -top-2 right-2 flex h-4 w-8 items-center rounded-full border border-white/40 px-0.5 transition-colors ${isActive ? 'justify-end bg-white/80' : 'justify-start bg-black/30'} ${disableToggle ? 'cursor-not-allowed opacity-40' : ''}`}
            onClick={(event) => {
              if (disableToggle) {
                event.stopPropagation();
                return;
              }
              event.stopPropagation();
              onToggle?.(!isActive);
            }}
            disabled={disableToggle}
          >
            <span
              className="h-3 w-3 rounded-full bg-white shadow transition-all duration-300"
            ></span>
          </button>
        )}

        {/* Move buttons for referent, sci-referent and boitage only - only show when unlocked */}
        {!timelineLocked && isActive && (type === 'referent' || type === 'sci-referent' || type === 'boitage') && (
          <div className="z-10 flex items-center gap-1">
            <button
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm border-none bg-white/20 text-[0.6rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/40"
              onClick={(e) => {
                e.stopPropagation();
                moveEvent(type, text, -0.5);
              }}
            >
              ◀
            </button>
            <button
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm border-none bg-white/20 text-[0.6rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/40"
              onClick={(e) => {
                e.stopPropagation();
                moveEvent(type, text, 0.5);
              }}
            >
              ▶
            </button>
          </div>
        )}
        
        <div className="text-center whitespace-nowrap">
          {text}
          {renderExactDateBadge(type, text)}
        </div>
        
        <div className="flex items-center gap-1">
          {!timelineLocked && isActive && renderDatePickerButton(type, text, position)}
          {/* Info button - always visible */}
          <button
            className="group/tooltip relative flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-full border-none bg-white/20 text-[0.65rem] text-white transition-all duration-300 hover:scale-110 hover:bg-white/30"
            title={getDateFromPosition(position)}
            onClick={(e) => {
              e.stopPropagation();
              if (!timelineLocked) {
                return;
              }
              if (type === 'newsletter') {
                navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
              } else if (type === 'referent') {
                navigate(ROUTES.COMMUNICATION_REFERENT_FORMATION);
              } else if (type === 'sci-referent') {
                navigate(ROUTES.MONITORING_SCIS);
              } else if (type === 'boitage') {
                navigate(ROUTES.PROSPECTING_BOITAGE_QUALIFICATION);
              }
            }}
          >
            <span role="img" aria-label="information">ℹ️</span>
            
            {/* Custom tooltip */}
            <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 transform rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover/tooltip:opacity-100">
              {getDateFromPosition(position)}
              <div className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 transform border-t-2 border-r-2 border-l-2 border-transparent border-t-gray-800"></div>
            </div>
          </button>
        </div>
      </div>
    );
  };

  const renderPlannerEvent = ({
    label,
    type,
    position,
    active,
    onToggle,
    disableToggle,
  }: {
    label: string;
    type: 'referent' | 'sci-referent' | 'boitage';
    position: number;
    active: boolean;
    onToggle: (active: boolean) => void;
    disableToggle?: boolean;
  }) => {
    if (timelineLocked && !active) {
      return null;
    }

    return renderEvent(label, type, position, active, onToggle, disableToggle);
  };

  const canToggleOffEvent = (type: DashboardEventType) => {
    const activationForType = safeEventActivation[type];
    const activeCount = Object.values(activationForType).filter(Boolean).length;
    return activeCount > 1;
  };

  const handleEventToggle = (type: DashboardEventType, label: string, nextActive: boolean) => {
    if (!nextActive && !canToggleOffEvent(type)) {
      showToast('warning', 'Conservez au moins une action active pour cette ligne.');
      return;
    }

    dispatch(updateEventActivation({ type, eventText: label, active: nextActive }));
  };

  const ROW_MODULE_ROUTES: Record<string, string> = {
    newsletter: ROUTES.COMMUNICATION_NEWSLETTER_LIST,
    'referent-mails': ROUTES.COMMUNICATION_REFERENT_FORMATION,
    'sci-mails': ROUTES.MONITORING_SCIS_OVERALL,
    boitage: ROUTES.PROSPECTING_BOITAGE_QUALIFICATION,
    dpe: ROUTES.PROSPECTING_NEW_OPPORTUNITIES,
    'ag-communication': ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_REFERENT,
    'property-communication': ROUTES.PROSPECTING_SCIS_TRANSACTION,
  };

  const renderOpenModuleButton = (rowKey: string) => {
    const route = ROW_MODULE_ROUTES[rowKey];
    if (!route) {
      return null;
    }

    return (
      <button
        type="button"
        className="mt-1.5 cursor-pointer border border-gray-200 bg-transparent px-2 py-0.5 text-[0.55rem] font-medium text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600"
        style={{ borderRadius: 4 }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(route);
        }}
      >
        + ouvrir le module
      </button>
    );
  };

  const isPeriodSpecificRow = (rowKey: string) => ['newsletter', 'referent-mails', 'sci-mails', 'boitage', 'follow-up-meetings'].includes(rowKey);

  const rowZClass = (rowKey: string) => (openPersonSelect === rowKey ? 'z-50' : 'z-0');
  const isRowRestricted = (rowKey: string) => Boolean(restrictedToRowKeys && !restrictedToRowKeys.includes(rowKey));
  const rowRestrictedClass = (rowKey: string) => (isRowRestricted(rowKey) ? 'pointer-events-none opacity-40' : '');

  const renderRowSendButton = (rowKey: string) => {
    if (!isPeriodSpecificRow(rowKey) || timelineLocked) {
      return null;
    }
    const userId = personAssignments[rowKey] ?? null;
    const eventTypePrefix = rowKey === 'referent-mails' ? 'referent-' : rowKey === 'sci-mails' ? 'sci-referent-' : `${rowKey}-`;
    const hasDate = Object.keys(exactDates).some((key) => key.startsWith(eventTypePrefix));
    const isDisabled = !userId || !hasDate || sendingRowKey === rowKey;

    return (
      <button
        className={`mt-1 flex w-full items-center justify-center gap-1 rounded px-1.5 py-0.5 text-[0.55rem] font-medium transition-all ${
          isDisabled
            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
            : 'cursor-pointer bg-rgpd-blue/10 text-rgpd-blue hover:bg-rgpd-blue/20'
        }`}
        disabled={isDisabled}
        title={!userId ? 'Attribuez un responsable' : !hasDate ? 'Définissez au moins une date' : 'Envoyer l\'invitation'}
        onClick={() => handleSendRowInvitation(rowKey)}
      >
        {sendingRowKey === rowKey ? (
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-b border-rgpd-blue" />
        ) : (
          <span role="img" aria-label="send" className="text-[0.6rem]">📨</span>
        )}
        Envoyer
      </button>
    );
  };

  const renderFollowUpSendButton = () => {
    if (timelineLocked) {
      return null;
    }
    const hasDate = Object.keys(exactDates).some((key) => key.startsWith('followup-'));
    const isDisabled = followUpAttendees.length === 0 || !hasDate || sendingRowKey === 'follow-up-meetings';

    return (
      <button
        className={`mt-1 flex w-full items-center justify-center gap-1 rounded px-1.5 py-0.5 text-[0.55rem] font-medium transition-all ${
          isDisabled
            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
            : 'cursor-pointer bg-rgpd-blue/10 text-rgpd-blue hover:bg-rgpd-blue/20'
        }`}
        disabled={isDisabled}
        title={followUpAttendees.length === 0 ? 'Aucun participant identifié' : !hasDate ? 'Définissez au moins une date' : 'Envoyer les invitations'}
        onClick={() => handleSendRowInvitation('follow-up-meetings')}
      >
        {sendingRowKey === 'follow-up-meetings' ? (
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-b border-rgpd-blue" />
        ) : (
          <span role="img" aria-label="send" className="text-[0.6rem]">📨</span>
        )}
        Envoyer
      </button>
    );
  };

  const renderPersonAssignment = (rowKey: string) => {
    const assignedUserId = personAssignments[rowKey] ?? null;

    if (timelineLocked) {
      const label = getUserLabel(assignedUserId);
      if (!label) {
        return null;
      }
      return (
        <div className="mt-1.5 flex flex-col gap-0.5">
          <span className="text-[0.5rem] font-bold tracking-widest text-gray-400 uppercase">Responsable</span>
          <span className="truncate rounded border border-gray-200 bg-white px-2 py-0.5 text-[0.6rem] font-medium text-gray-700" title={label}>
            {label}
          </span>
        </div>
      );
    }

    return (
      <div className="relative mt-1">
        <Select
          options={userOptions}
          value={getUserOptionValue(assignedUserId)}
          onChange={(option) => {
            dispatch(updatePersonAssignment({ rowKey, userId: option?.value ?? null }));
            setOpenPersonSelect(null);
          }}
          onMenuOpen={() => setOpenPersonSelect(rowKey)}
          onMenuClose={() => setOpenPersonSelect((prev) => (prev === rowKey ? null : prev))}
          placeholder="Responsable"
          noOptionsMessage={() => 'Aucun résultat'}
          isClearable
          isSearchable
          maxMenuHeight={200}
          menuPlacement="auto"
          menuPortalTarget={document.body}
          styles={{
            control: (base) => ({ ...base, fontSize: '0.65rem', minHeight: '24px', padding: '0' }),
            valueContainer: (base) => ({ ...base, padding: '0 4px' }),
            input: (base) => ({ ...base, margin: 0, padding: 0 }),
            indicatorsContainer: (base) => ({ ...base, height: '24px' }),
            dropdownIndicator: (base) => ({ ...base, padding: '2px' }),
            clearIndicator: (base) => ({ ...base, padding: '2px' }),
            option: (base) => ({ ...base, fontSize: '0.65rem', padding: '4px 8px' }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
        {renderRowSendButton(rowKey)}
      </div>
    );
  };

  return (
    <>
      <div className="relative rounded-xl border border-white/20 bg-white p-4 shadow-xl transition-all duration-300 hover:shadow-2xl sm:p-8">
        {/* Gradient border top */}
        <div className="absolute top-0 right-0 left-0 h-1 rounded-t-xl bg-rgpd-blue" />
        
        <div className="relative mb-6 flex items-center gap-3 sm:gap-6">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-rgpd-blue/20 bg-rgpd-blue-light text-2xl shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:h-16 sm:w-16 sm:text-3xl">
            <span role="img" aria-label="calendar" className="drop-shadow-sm filter">📅</span>
          </div>
          <div className="flex-1 text-left">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
              <div>
                <h2 className="mb-1 text-base leading-tight font-semibold text-gray-800 sm:mr-[150px] sm:text-2xl">
                  Votre Planning Stratégique personnalisable des actions à mener sur les 12 prochains mois
                </h2>
                <div className="text-xs text-gray-600 italic sm:text-base">
                  Découvrez les 7 actions complémentaires déjà préparées et planifiées que vous aurez à suivre
                </div>
              </div>
              <div className="z-10 ml-auto shrink-0 sm:absolute sm:top-1 sm:-right-6">
                <button
                  onClick={handleTogglePlanning}
                  aria-expanded={planningActivated}
                  aria-controls="planning-calendar-section"
                  className={`${
                    planningActivated 
                      ? 'bg-rgpd-green hover:bg-rgpd-green-dark' 
                      : 'bg-rgpd-blue hover:bg-rgpd-blue-dark'
                  } inline-flex cursor-pointer items-center gap-2 rounded-md border-none px-3 py-1.5 text-xs leading-none font-medium whitespace-nowrap text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg`}
                >
                  {planningActivated 
                    ? (<><span role="img" aria-label="check mark">✅</span> <span>Réduire le planning</span> <span>▴</span></>)
                    : (<><span role="img" aria-label="calendar">📅</span> <span>Etendre le planning</span> <span>▾</span></>)
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
        
        

        {/* Calendar Content - Now inside the same card */}
        <div id="planning-calendar-section" className={`relative transition-all duration-700 ease-in-out ${
          planningActivated ? 'max-h-[20000px] overflow-visible opacity-100' : 'max-h-0 overflow-hidden opacity-0'
        }`}>
          <div className="relative">
            <div className={`mb-8 flex flex-wrap items-center justify-between gap-4 ${!hasAnyModuleAccess(company) ? 'pointer-events-none' : ''}`}>
              <div className="flex-1 text-base font-semibold text-gray-800 sm:text-xl">
                <span role="img" aria-label="calendar" className="mr-2 drop-shadow-sm filter">📅</span> Calendrier des Actions sur 12 Mois
              </div>
              
              {/* Show loading state while company data is not available */}
              {isCompanyLoading ? (
                <div className="my-8 flex items-center gap-3 text-gray-500">
                  <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-gray-500"></div>
                  <span className="text-sm">Chargement des données...</span>
                </div>
              ) : (
                <div className="flex w-full flex-wrap items-center justify-center gap-3 sm:w-auto sm:justify-end sm:gap-6">
                  {!timelineLocked && (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={handleSendAllInvitations}
                        disabled={isSendingInvitations || !hasAnyInvitableEvents}
                        className={`relative inline-flex h-12 w-12 transform items-center justify-center rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                          isSendingInvitations || !hasAnyInvitableEvents
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'bg-blue-100 text-blue-600 hover:bg-blue-200 focus:ring-blue-500'
                        }`}
                        title={!hasAnyInvitableEvents ? 'Attribuez des responsables et des dates pour envoyer les invitations' : 'Envoyer toutes les invitations'}
                      >
                        {isSendingInvitations ? (
                          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-b-transparent" />
                        ) : (
                          <span role="img" aria-label="send invitations" className="text-xl">📨</span>
                        )}
                      </button>
                      <span className={`text-xs font-medium ${
                        isSendingInvitations ? 'animate-pulse text-blue-600' : hasAnyInvitableEvents ? 'text-blue-600' : 'text-gray-400'
                      }`}>
                        {isSendingInvitations ? 'Envoi...' : 'Invitations'}
                      </span>
                    </div>
                  )}

                  {!timelineLocked && (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={handleDownloadBlankPdf}
                        disabled={isGeneratingBlankPdf}
                        className={`relative inline-flex h-12 w-12 transform items-center justify-center rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                          isGeneratingBlankPdf
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'bg-teal-100 text-teal-600 hover:bg-teal-200 focus:ring-teal-500'
                        }`}
                        title="Télécharger le planning vierge à remplir"
                      >
                        {isGeneratingBlankPdf ? (
                          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-teal-600 border-b-transparent" />
                        ) : (
                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                        )}
                      </button>
                      <span className={`text-xs font-medium ${
                        isGeneratingBlankPdf ? 'animate-pulse text-teal-600' : 'text-teal-600'
                      }`}>
                        {isGeneratingBlankPdf ? 'Génération...' : 'Planning vierge'}
                      </span>
                    </div>
                  )}

                  {!timelineLocked && (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={handleApplyAutoDates}
                        disabled={missingExactDatesCount === 0}
                        className={`relative inline-flex h-12 w-12 transform items-center justify-center rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                          missingExactDatesCount === 0
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'bg-amber-100 text-amber-600 hover:bg-amber-200 focus:ring-amber-500'
                        }`}
                        title={missingExactDatesCount === 0
                          ? 'Toutes les dates sont déjà définies'
                          : `Définir le mardi le plus proche pour ${missingExactDatesCount} événement(s) sans date`}
                      >
                        <span role="img" aria-label="auto date" className="text-2xl">🪄</span>
                        {missingExactDatesCount > 0 && (
                          <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-600 px-1 text-[0.6rem] font-bold text-white shadow">
                            {missingExactDatesCount}
                          </span>
                        )}
                      </button>
                      <span className={`text-xs font-medium ${
                        missingExactDatesCount === 0 ? 'text-gray-400' : 'text-amber-600'
                      }`}>
                        Dates auto
                      </span>
                    </div>
                  )}

                  {/* Timeline Lock/Unlock/Save Button */}
                  <div className="flex flex-col items-center gap-2">
                    {timelineLocked ? (
                    /* Unlock Button - When timeline is locked */
                      <button
                        onClick={handleToggleTimelineLock}
                        className="relative inline-flex h-12 w-12 transform items-center justify-center rounded-full bg-gray-100 text-gray-600 shadow-lg transition-all duration-300 ease-in-out hover:scale-105 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none"
                        title="Déverrouiller l'édition"
                      >
                        <div className="relative">
                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 10h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v4H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V12c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v4H9V6z"/>
                          </svg>
                        </div>
                      </button>
                    ) : isSavingAndLocking ? (
                    /* Saving Animation - During save and lock */
                      <button
                        disabled
                        className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600 shadow-lg transition-all duration-300 ease-in-out"
                        title="Sauvegarde et verrouillage..."
                      >
                        <div className="relative">
                          {/* Lock icon with save overlay */}
                          <svg className="h-6 w-6 animate-pulse" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 10h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h2c0-1.66 1.34-3 3-3s3 1.34 3 3v4H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V12c0-1.1-.9-2-2-2z"/>
                          </svg>
                        
                          {/* Rotating save indicator overlay */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-2 border-green-300 border-t-green-600 opacity-75"></div>
                          </div>
                        </div>
                      </button>
                    ) : (
                    /* Save and Lock Button - When timeline is unlocked */
                      <button
                        onClick={handleSaveAndLock}
                        className="relative inline-flex h-12 w-12 transform items-center justify-center rounded-full bg-green-100 text-green-600 shadow-lg transition-all duration-300 ease-in-out hover:scale-105 hover:bg-green-200 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
                        title="Verrouiller et sauvegarder"
                      >
                        <div className="relative">
                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 10h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h2c0-1.66 1.34-3 3-3s3 1.34 3 3v4H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V12c0-1.1-.9-2-2-2z"/>
                          </svg>
                        </div>
                      </button>
                    )}
                  
                    <span className={`text-xs font-medium ${
                      timelineLocked 
                        ? 'text-gray-600' 
                        : isSavingAndLocking 
                          ? 'animate-pulse text-green-600' 
                          : 'text-green-600'
                    }`}>
                      {timelineLocked 
                        ? 'Verrouillé' 
                        : isSavingAndLocking 
                          ? 'Sauvegarde...' 
                          : 'Sauvegarder'
                      }
                    </span>
                  </div>
                
                  <div className="flex flex-col items-stretch gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
                    <label htmlFor="moisLancement" className="text-center text-sm font-medium whitespace-nowrap text-gray-800 sm:text-left">
                      Choisissez votre mois de lancement :
                    </label>
                    <select
                      id="moisLancement"
                      value={startMonth}
                      disabled={timelineLocked}
                      onChange={(e) => {
                        const newMonth = parseInt(e.target.value, 10);
                        handleStartMonthSelect(newMonth);
                      }}
                      className={`min-w-[120px] rounded-md border border-gray-300 p-2 text-sm focus:outline-none ${
                        timelineLocked 
                          ? 'cursor-not-allowed bg-gray-100 text-gray-500' 
                          : 'bg-white text-gray-800 focus:border-rgpd-blue'
                      }`}
                    >
                      {MOIS_NOMS.map((month, index) => (
                        <option key={index + 1} value={index + 1}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
            
            {/* Calendar Content - Strategy explanation always visible */}
            <div className="mb-8 rounded-lg border border-blue-300 bg-blue-100 p-4 text-center sm:p-6">
              <h4 className="mb-3 text-base font-semibold text-rgpd-blue-light"><span role="img" aria-label="target" className="mr-2 drop-shadow-sm filter">🎯</span> <span className="text-sm">Votre Stratégie Globale sur 12 Mois</span></h4>
              <p className="m-0 text-sm leading-relaxed text-rgpd-blue-dark">
                Découvrez ci-dessous les <strong>7 actions complémentaires</strong> réparties sur les 12 prochains mois. 
                Cette planification vous permet d'avoir une <strong>véritable stratégie globale</strong> de développement commercial 
                tout en maintenant un contact régulier et professionnel avec vos copropriétaires. Cette stratégie sera définie et adaptée lors de la mise en place avec votre contact chez Déclarations Juridiques.
              </p>
            </div>
            
          </div>
          
          {/* Calendar Grid - horizontally scrollable on mobile so the dense 12-month timeline stays readable; behaves exactly as before on sm+ */}
          <div className="overflow-x-auto pb-2 sm:overflow-x-visible sm:pb-0">
            <div className="relative min-w-[900px] sm:min-w-0">
          {/* Month Headers */}
          <div ref={monthHeaderRef} className="relative mb-4 grid grid-cols-[225px_repeat(12,1fr)] gap-2 px-4">
            <div></div>
            {Array.from({ length: 12 }, (_, i) => {
              const monthIndex = (startMonth + i - 1) % 12;
              return (
                <div
                  key={i}
                  className="rounded-md bg-rgpd-blue py-2 text-center text-sm font-medium text-white"
                >
                  {MOIS_COURTS[monthIndex]}
                </div>
              );
            })}
          </div>

          {/* Today vertical line overlayed on the whole planning area */}
          {hasAnyModuleAccess(company) && (() => {
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth(); // 0-based
            const startMonthIndex = startMonth - 1; // Convert to 0-based
            const columnPosition = ((currentMonth - startMonthIndex + 12) % 12) + 1; // 1-12 range
            // Position calculation aligned with the header grid padding/gap and first column
            const leftPosition = `calc(245px + 0.5rem + ${(columnPosition - 1)} * (100% - 225px - 1.5rem) / 12 + (100% - 225px - 1.5rem) / 24)`; // Center in column

            return (
              <div
                className="pointer-events-none absolute w-1 bg-red-500/50 shadow-lg"
                style={{
                  left: leftPosition,
                  top: todayLineTop,
                  bottom: 0,
                  transform: 'translateX(-50%)',
                  zIndex: 50,
                }}
                title={`Aujourd'hui: ${MOIS_NOMS[currentMonth]} ${currentDate.getFullYear()}`}
              >
                {/* Small indicator at the top */}
                <div className="absolute -top-2 left-1/2 h-3 w-3 -translate-x-1/2 transform rounded-full border-2 border-white bg-red-500 shadow-md"></div>
              </div>
            );
          })()}
          
          {/* Skeletons while loading company data */}
          <div className={`relative ${!hasAnyModuleAccess(company) ? 'pointer-events-none [&_.planning-timeline-cell]:blur-[2px]' : ''}`}>
          {!hasAnyModuleAccess(company) && (
            <div className="pointer-events-none absolute inset-y-0 right-0 left-[158px] z-10 flex items-start justify-center rounded-lg">
              <div className="mt-40 flex max-w-[75%] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-6 shadow-lg">
                <p className="text-center text-sm font-bold text-rgpd-blue-dark">Votre planning stratégique personnalisé</p>
                <p className="mt-2 px-6 text-center text-sm text-gray-500">Ce planning sera défini et adapté avec votre interlocuteur chez Déclarations Juridiques lors de votre mise en place. Il vous permettra de planifier vos actions sur l'année à venir.</p>
              </div>
            </div>
          )}
          {isCompanyLoading ? (
            <>
              {/* Newsletter Row Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-12 rounded bg-gray-200" />
                </div>
                <div className="h-16 rounded-md border-2 border-dashed bg-gray-100" />
              </div>

              {/* Referent Mail Row Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-8 rounded bg-gray-200" />
                </div>
                <div className="h-16 rounded-md border-2 border-dashed bg-gray-100" />
              </div>

              {/* AG Communication Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-12 rounded bg-gray-200" />
                </div>
                <div className="h-9 rounded-full bg-gray-200" />
              </div>

              {/* DPE Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-12 rounded bg-gray-200" />
                </div>
                <div className="h-9 rounded-full bg-gray-200" />
              </div>

              {/* SCI Referent Row Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-12 rounded bg-gray-200" />
                </div>
                <div className="h-16 rounded-md border-2 border-dashed bg-gray-100" />
              </div>

              {/* Boîtage Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-6 rounded bg-gray-200" />
                </div>
                <div className="h-16 rounded-md border-2 border-dashed bg-gray-100" />
              </div>

              {/* Property Communication Skeleton */}
              <div className="mb-5 grid animate-pulse grid-cols-[225px_1fr] items-center gap-2">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-4 w-6 rounded bg-gray-200" />
                </div>
                <div className="h-9 rounded-full bg-gray-200" />
              </div>
            </>
          ) : (
            <>
              {/* Newsletter Row */}
              <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('newsletter')} ${rowRestrictedClass('newsletter')}`}>
                <div className="flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
                  {renderReferentTag('newsletter', isAdmin)}
                  <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                    Envoi Newsletter Trimestriel
                    {renderPersonAssignment('newsletter')}
                    {renderOpenModuleButton('newsletter')}
                  </div>
                </div>
                <div className={`planning-timeline-cell relative h-16 rounded-md border-2 border-dashed bg-gray-100 transition-all duration-300 ${timelineLocked ? 'border-gray-300' : 'border-rgpd-blue/40 bg-rgpd-blue-light/10'}`}>
                  {eventPositions.newsletter && Object.entries(eventPositions.newsletter).map(([eventName, eventData], index) =>
                    renderNewsletterEvent(eventName, eventData.position, eventData.theme, index > 0), // First newsletter (DPE) is not editable
                  )}
                </div>
              </div>

              {/* Referent Mail Row */}
              {(!timelineLocked || rowVisibility['referent-mails']) && (timelineLocked ? safeEventActivation.referent['Référent 1'] || safeEventActivation.referent['Référent 2'] || safeEventActivation.referent['Référent 3'] : true) && (
                <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('referent-mails')} ${rowRestrictedClass('referent-mails')} ${!timelineLocked && !rowVisibility['referent-mails'] ? 'opacity-50' : ''}`}>
                  <div className="relative flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
                    {renderReferentTag('referent-mails', isAdmin)}
                    <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                      {!timelineLocked && (
                        <label
                          className="absolute top-1.5 right-2 inline-flex cursor-pointer items-center select-none"
                          title={rowVisibility['referent-mails'] ? 'Masquer cette ligne' : 'Afficher cette ligne'}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={rowVisibility['referent-mails']}
                            onChange={(e) => setRowVisibility(prev => ({ ...prev, 'referent-mails': e.target.checked }))}
                            aria-label="Afficher/Masquer la ligne"
                          />
                          <div className={`relative h-5 w-10 rounded-full shadow-inner transition-colors ${rowVisibility['referent-mails'] ? 'bg-blue-static' : 'bg-gray-300'}`}>
                            <div className={`absolute top-1/2 left-1 -translate-y-1/2 ${rowVisibility['referent-mails'] ? 'translate-x-5' : 'translate-x-0'} h-4 w-4 rounded-full bg-white shadow-md transition-all`}></div>
                          </div>
                        </label>
                      )}
                      Envoi de mails présentation du référent
                      {renderPersonAssignment('referent-mails')}
                      {renderOpenModuleButton('referent-mails')}
                    </div>
                  </div>
                  <div className={`planning-timeline-cell relative h-16 rounded-md border-2 border-dashed bg-gray-100 transition-all duration-300 ${timelineLocked ? 'border-gray-300' : 'border-rgpd-blue/40 bg-rgpd-blue-light/10'}`}>
                    {renderPlannerEvent({
                      label: 'Référent 1',
                      type: 'referent',
                      position: safeEventPositions.referent['Référent 1'],
                      active: safeEventActivation.referent['Référent 1'],
                      onToggle: (active) => handleEventToggle('referent', 'Référent 1', active),
                      disableToggle: !canToggleOffEvent('referent') && safeEventActivation.referent['Référent 1'],
                    })}
                    {renderPlannerEvent({
                      label: 'Référent 2',
                      type: 'referent',
                      position: safeEventPositions.referent['Référent 2'],
                      active: safeEventActivation.referent['Référent 2'],
                      onToggle: (active) => handleEventToggle('referent', 'Référent 2', active),
                      disableToggle: !canToggleOffEvent('referent') && safeEventActivation.referent['Référent 2'],
                    })}
                    {renderPlannerEvent({
                      label: 'Référent 3',
                      type: 'referent',
                      position: safeEventPositions.referent['Référent 3'],
                      active: safeEventActivation.referent['Référent 3'],
                      onToggle: (active) => handleEventToggle('referent', 'Référent 3', active),
                      disableToggle: !canToggleOffEvent('referent') && safeEventActivation.referent['Référent 3'],
                    })}
                  </div>
                </div>
              )}
              
              {/* AG Communication Row (#3) */}
          {(!timelineLocked || rowVisibility['ag-communication']) && (
            <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('ag-communication')} ${rowRestrictedClass('ag-communication')} ${!timelineLocked && !rowVisibility['ag-communication'] ? 'opacity-50' : ''}`}>
              <div className="relative flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
                {renderReferentTag('ag-communication', isAdmin)}
                <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                  {!timelineLocked && (
                    <label
                      className="absolute top-1.5 right-2 inline-flex cursor-pointer items-center select-none"
                      title={rowVisibility['ag-communication'] ? 'Masquer cette ligne' : 'Afficher cette ligne'}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={rowVisibility['ag-communication']}
                        onChange={(e) => setRowVisibility(prev => ({ ...prev, 'ag-communication': e.target.checked }))}
                        aria-label="Afficher/Masquer la ligne"
                      />
                      <div className={`relative h-5 w-10 rounded-full shadow-inner transition-colors ${rowVisibility['ag-communication'] ? 'bg-blue-static' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1/2 left-1 -translate-y-1/2 ${rowVisibility['ag-communication'] ? 'translate-x-5' : 'translate-x-0'} h-4 w-4 rounded-full bg-white shadow-md transition-all`}></div>
                      </div>
                    </label>
                  )}
                  Communication lors des AG
                  {renderPersonAssignment('ag-communication')}
                  {renderOpenModuleButton('ag-communication')}
                </div>
              </div>
              <div className="planning-timeline-cell flex items-center">
                <div
                  className={`relative flex min-h-10 w-full items-center justify-between rounded-full bg-green-600 px-8 text-sm font-medium text-white shadow-lg transition-all duration-300 ${
                    timelineLocked ? 'cursor-pointer hover:-translate-y-0.5 hover:bg-green-700 hover:shadow-xl' : 'cursor-default'
                  }`}
                  onClick={() => {
                    if (timelineLocked) {
                      navigate(ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_REFERENT);
                    }
                  }}
                >
                  <div className="absolute left-3 animate-pulse text-base">→</div>
                  <div className="mx-2 flex-1 text-center">
                    La communication pendant les AG : documents et argumentaire
                  </div>
                  <button
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-none bg-white/20 text-[0.7rem] text-white transition-all duration-300 ${
                      timelineLocked ? 'cursor-pointer hover:scale-110 hover:bg-white/30' : 'cursor-default'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (timelineLocked) {
                        navigate(ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_REFERENT);
                      }
                    }}
                  >
                    <span role="img" aria-label="information">ℹ️</span>
                  </button>
                  <div className="absolute right-3 animate-pulse text-base" style={{ animationDelay: '1s' }}>→</div>
                </div>
              </div>
            </div>
          )}

          {/* DPE Row (#4) */}
          <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('dpe')} ${rowRestrictedClass('dpe')}`}>
            <div className="flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
              {renderReferentTag('dpe', isAdmin)}
              <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                DPE (traitement à la semaine)
                {renderPersonAssignment('dpe')}
                {renderOpenModuleButton('dpe')}
              </div>
            </div>
            <div className="planning-timeline-cell flex items-center">
              <div
                className={`relative flex min-h-10 w-full items-center justify-between rounded-full bg-violet-600 px-8 text-sm font-medium text-white shadow-lg transition-all duration-300 ${
                  timelineLocked ? 'cursor-pointer hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-xl' : 'cursor-default'
                }`}
                onClick={() => {
                  if (timelineLocked) {
                    navigate(ROUTES.PROSPECTING_NEW_OPPORTUNITIES);
                  }
                }}
              >
                <div className="absolute left-3 animate-pulse text-base">→</div>
                <div className="mx-2 flex-1 text-center">
                  Détection automatique chaque mercredi - Nouveaux DPE dans vos copropriétés
                </div>
                <button
                  className={`flex h-6 w-6 items-center justify-center rounded-full border-none bg-white/20 text-[0.7rem] text-white transition-all duration-300 ${
                    timelineLocked ? 'cursor-pointer hover:scale-110 hover:bg-white/30' : 'cursor-default'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (timelineLocked) {
                      navigate(ROUTES.PROSPECTING_NEW_OPPORTUNITIES);
                    }
                  }}
                >
                  <span role="img" aria-label="information">ℹ️</span>
                </button>
                <div className="absolute right-3 animate-pulse text-base" style={{ animationDelay: '1s' }}>→</div>
              </div>
            </div>
          </div>

              {/* SCI Referent Row (#5) */}
              {(!timelineLocked || rowVisibility['sci-mails']) && (timelineLocked ? safeEventActivation['sci-referent']['SCI 1'] || safeEventActivation['sci-referent']['SCI 2'] || safeEventActivation['sci-referent']['SCI 3'] : true) && (
                <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('sci-mails')} ${rowRestrictedClass('sci-mails')} ${!timelineLocked && !rowVisibility['sci-mails'] ? 'opacity-50' : ''}`}>
                  <div className="relative flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
                    {renderReferentTag('sci-mails')}
                    <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                      {!timelineLocked && (
                        <label
                          className="absolute top-1.5 right-2 inline-flex cursor-pointer items-center select-none"
                          title={rowVisibility['sci-mails'] ? 'Masquer cette ligne' : 'Afficher cette ligne'}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={rowVisibility['sci-mails']}
                            onChange={(e) => setRowVisibility(prev => ({ ...prev, 'sci-mails': e.target.checked }))}
                            aria-label="Afficher/Masquer la ligne"
                          />
                          <div className={`relative h-5 w-10 rounded-full shadow-inner transition-colors ${rowVisibility['sci-mails'] ? 'bg-blue-static' : 'bg-gray-300'}`}>
                            <div className={`absolute top-1/2 left-1 -translate-y-1/2 ${rowVisibility['sci-mails'] ? 'translate-x-5' : 'translate-x-0'} h-4 w-4 rounded-full bg-white shadow-md transition-all`}></div>
                          </div>
                        </label>
                      )}
                      Envois de mails aux SCI (transaction / gestion)
                      {renderPersonAssignment('sci-mails')}
                      {renderOpenModuleButton('sci-mails')}
                    </div>
                  </div>
                  <div className={`planning-timeline-cell relative h-16 rounded-md border-2 border-dashed bg-gray-100 transition-all duration-300 ${timelineLocked ? 'border-gray-300' : 'border-rgpd-blue/40 bg-rgpd-blue-light/10'}`}>
                    {renderPlannerEvent({
                      label: 'SCI 1',
                      type: 'sci-referent',
                      position: safeEventPositions['sci-referent']['SCI 1'],
                      active: safeEventActivation['sci-referent']['SCI 1'],
                      onToggle: (active) => handleEventToggle('sci-referent', 'SCI 1', active),
                      disableToggle: !canToggleOffEvent('sci-referent') && safeEventActivation['sci-referent']['SCI 1'],
                    })}
                    {renderPlannerEvent({
                      label: 'SCI 2',
                      type: 'sci-referent',
                      position: safeEventPositions['sci-referent']['SCI 2'],
                      active: safeEventActivation['sci-referent']['SCI 2'],
                      onToggle: (active) => handleEventToggle('sci-referent', 'SCI 2', active),
                      disableToggle: !canToggleOffEvent('sci-referent') && safeEventActivation['sci-referent']['SCI 2'],
                    })}
                    {renderPlannerEvent({
                      label: 'SCI 3',
                      type: 'sci-referent',
                      position: safeEventPositions['sci-referent']['SCI 3'],
                      active: safeEventActivation['sci-referent']['SCI 3'],
                      onToggle: (active) => handleEventToggle('sci-referent', 'SCI 3', active),
                      disableToggle: !canToggleOffEvent('sci-referent') && safeEventActivation['sci-referent']['SCI 3'],
                    })}
                  </div>
                </div>
              )}

              {/* Boîtage Row (#6) */}
          {(!timelineLocked || rowVisibility.boitage) && (timelineLocked ? safeEventActivation.boitage['Boîtage 1'] || safeEventActivation.boitage['Boîtage 2'] : true) && (
            <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('boitage')} ${rowRestrictedClass('boitage')} ${!timelineLocked && !rowVisibility.boitage ? 'opacity-50' : ''}`}>
              <div className="relative flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
                {renderReferentTag('boitage')}
                <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                  {!timelineLocked && (
                    <label
                      className="absolute top-1.5 right-2 inline-flex cursor-pointer items-center select-none"
                      title={rowVisibility.boitage ? 'Masquer cette ligne' : 'Afficher cette ligne'}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={rowVisibility.boitage}
                        onChange={(e) => setRowVisibility(prev => ({ ...prev, boitage: e.target.checked }))}
                        aria-label="Afficher/Masquer la ligne"
                      />
                      <div className={`relative h-5 w-10 rounded-full shadow-inner transition-colors ${rowVisibility.boitage ? 'bg-blue-static' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1/2 left-1 -translate-y-1/2 ${rowVisibility.boitage ? 'translate-x-5' : 'translate-x-0'} h-4 w-4 rounded-full bg-white shadow-md transition-all`}></div>
                      </div>
                    </label>
                  )}
                  BOÎTAGE dans les copropriétés à fort potentiel
                  {renderPersonAssignment('boitage')}
                  {renderOpenModuleButton('boitage')}
                </div>
              </div>
                  <div className={`planning-timeline-cell relative h-16 rounded-md border-2 border-dashed bg-gray-100 transition-all duration-300 ${timelineLocked ? 'border-gray-300' : 'border-rgpd-blue/40 bg-rgpd-blue-light/10'}`}>
                {renderPlannerEvent({
                  label: 'Boîtage 1',
                  type: 'boitage',
                  position: safeEventPositions.boitage['Boîtage 1'],
                  active: safeEventActivation.boitage['Boîtage 1'],
                  onToggle: (active) => handleEventToggle('boitage', 'Boîtage 1', active),
                  disableToggle: !canToggleOffEvent('boitage') && safeEventActivation.boitage['Boîtage 1'],
                })}
                {renderPlannerEvent({
                  label: 'Boîtage 2',
                  type: 'boitage',
                  position: safeEventPositions.boitage['Boîtage 2'],
                  active: safeEventActivation.boitage['Boîtage 2'],
                  onToggle: (active) => handleEventToggle('boitage', 'Boîtage 2', active),
                  disableToggle: !canToggleOffEvent('boitage') && safeEventActivation.boitage['Boîtage 2'],
                })}
              </div>
            </div>
          )}

          {/* Property Communication Row (#7) */}
          {(!timelineLocked || rowVisibility['property-communication']) && (
            <div className={`relative mb-5 grid grid-cols-[225px_1fr] items-center gap-2 ${rowZClass('property-communication')} ${rowRestrictedClass('property-communication')} ${!timelineLocked && !rowVisibility['property-communication'] ? 'opacity-50' : ''}`}>
              <div className="relative flex items-start gap-3 overflow-visible rounded-md border border-gray-200 bg-gray-50 p-3">
                {renderReferentTag('property-communication')}
                <div className="flex min-w-0 flex-1 flex-col text-left text-sm font-medium text-gray-800">
                  {!timelineLocked && (
                    <label
                      className="absolute top-1.5 right-2 inline-flex cursor-pointer items-center select-none"
                      title={rowVisibility['property-communication'] ? 'Masquer cette ligne' : 'Afficher cette ligne'}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={rowVisibility['property-communication']}
                        onChange={(e) => setRowVisibility(prev => ({ ...prev, 'property-communication': e.target.checked }))}
                        aria-label="Afficher/Masquer la ligne"
                      />
                      <div className={`relative h-5 w-10 rounded-full shadow-inner transition-colors ${rowVisibility['property-communication'] ? 'bg-blue-static' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1/2 left-1 -translate-y-1/2 ${rowVisibility['property-communication'] ? 'translate-x-5' : 'translate-x-0'} h-4 w-4 rounded-full bg-white shadow-md transition-all`}></div>
                      </div>
                    </label>
                  )}
                  Communication sur vos biens à vendre
                  {renderPersonAssignment('property-communication')}
                  {renderOpenModuleButton('property-communication')}
                </div>
              </div>
              <div className="planning-timeline-cell flex items-center">
                <div
                  className={`relative flex min-h-10 w-full items-center justify-between rounded-full bg-violet-600 px-8 text-sm font-medium text-white shadow-lg transition-all duration-300 ${
                    timelineLocked ? 'cursor-pointer hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-xl' : 'cursor-default'
                  }`}
                  onClick={() => {
                    if (timelineLocked) {
                      navigate(ROUTES.PROSPECTING_SCIS_TRANSACTION);
                    }
                  }}
                >
                  <div className="absolute left-3 animate-pulse text-base">→</div>
                  <div className="mx-2 flex-1 text-center">
                    Quand vous avez un mandat - information aux copropriétaires sur le bien à vendre/louer dans leur copro
                  </div>
                  <button
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-none bg-white/20 text-[0.7rem] text-white transition-all duration-300 ${
                      timelineLocked ? 'cursor-pointer hover:scale-110 hover:bg-white/30' : 'cursor-default'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (timelineLocked) {
                        navigate(ROUTES.PROSPECTING_SCIS_TRANSACTION);
                      }
                    }}
                  >
                    <span role="img" aria-label="information">ℹ️</span>
                  </button>
                  <div className="absolute right-3 animate-pulse text-base" style={{ animationDelay: '1s' }}>→</div>
                </div>
              </div>
            </div>
          )}

          {/* Follow-Up Meetings Row — Premium dark card */}
          <div className={`relative mt-4 mb-5 rounded-xl shadow-lg ${rowZClass('follow-up-meetings')} ${rowRestrictedClass('follow-up-meetings')}`}>
            <div className="flex min-h-[80px] items-stretch rounded-xl" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)' }}>
              {/* Left sidebar */}
              <div className="flex w-[225px] shrink-0 flex-col items-center justify-center gap-1.5 border-r border-white/15 px-3 py-3.5" style={{ background: 'rgba(0,0,0,0.15)' }}>
                <div className="text-[8px] font-extrabold tracking-[0.14em] text-white/55 uppercase">SUIVI</div>
                <div className="text-[22px]"><span role="img" aria-label="handshake">🤝</span></div>
                <div className="rounded-full border border-white/30 bg-white/18 px-2.5 py-0.5 text-[10px] font-extrabold whitespace-nowrap text-white">
                  {safeEventPositions.followup ? `${Object.keys(safeEventPositions.followup).length} points / an` : '2 points / an'}
                </div>
                {renderFollowUpSendButton()}
              </div>

              {/* Right content */}
              <div className="flex flex-1 flex-col justify-center">
                <div className="px-4 pt-2.5 pb-1.5">
                  <div className="mb-1 text-[9px] font-extrabold tracking-[0.14em] text-white/50 uppercase">Accompagnement</div>
                  <div className="mb-1 text-[13px] font-black tracking-wide text-white uppercase">
                    Points d{'\u2019'}avancement avec Déclarations Juridiques
                  </div>
                  <div className="mb-2 h-0.5 w-8 rounded-sm bg-white/30"></div>
                  <div className="text-[10px] text-white/70">
                    {followUpAttendees.length > 0
                      ? <>Participants : {followUpAttendees.map((u) => `${u.firstname} ${u.lastname ? u.lastname.toUpperCase() : ''}`).join(' · ')}</>
                      : <span className="text-white/40 italic">Aucun responsable attribué</span>
                    }
                  </div>
                </div>

                {/* Event pills row */}
                <div className="planning-timeline-cell relative flex px-4 pb-3">
                  {safeEventPositions.followup && (() => {
                    const followupEntries = Object.entries(safeEventPositions.followup);
                    const months = Array.from({ length: 12 }, (_, i) => {
                      const monthIndex = (startMonth + i - 1) % 12;
                      return MOIS_COURTS[monthIndex];
                    });
                    return months.map((monthLabel, i) => {
                      const matchingEvent = followupEntries.find(([, pos]) => Math.round(pos as number) === i);
                      if (matchingEvent) {
                        const [eventName, position] = matchingEvent;
                        const pos = position as number;
                        return (
                          <div key={`followup-cell-${i}`} className="flex-1 shrink-0 px-0.5">
                            <div
                              className={`group relative flex items-center justify-between gap-1 rounded-full bg-emerald-600 px-2 py-1.5 text-[10.5px] font-bold whitespace-nowrap text-white ${
                                timelineLocked ? 'cursor-default' : 'cursor-pointer hover:bg-emerald-700'
                              }`}
                            >
                              {!timelineLocked && (
                                <div className="z-10 flex items-center gap-0.5">
                                  <button
                                    className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded-sm border-none bg-white/20 text-[0.5rem] text-white transition-all hover:scale-110 hover:bg-white/40"
                                    onClick={(e) => { e.stopPropagation(); moveEvent('followup', eventName, -0.5); }}
                                  >◀</button>
                                  <button
                                    className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded-sm border-none bg-white/20 text-[0.5rem] text-white transition-all hover:scale-110 hover:bg-white/40"
                                    onClick={(e) => { e.stopPropagation(); moveEvent('followup', eventName, 0.5); }}
                                  >▶</button>
                                </div>
                              )}
                              <span>{eventName}</span>
                              {renderExactDateBadge('followup', eventName)}
                              <div className="flex items-center gap-0.5">
                                {!timelineLocked && renderDatePickerButton('followup', eventName, pos)}
                                <button
                                  className="group/tooltip relative flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded-full border-none bg-white/30 text-[9px] text-white transition-all hover:scale-110 hover:bg-white/40"
                                  title={getDateFromPosition(pos)}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span role="img" aria-label="information">ℹ️</span>
                                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity group-hover/tooltip:opacity-100">
                                    {getDateFromPosition(pos)}
                                    <div className="absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 border-t-2 border-r-2 border-l-2 border-transparent border-t-gray-800"></div>
                                  </div>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={`followup-empty-${i}`} className="flex-1 shrink-0 px-0.5">
                          <div className="h-9 rounded-lg border-[1.5px] border-dashed border-white/25" style={{ background: 'rgba(255,255,255,0.05)' }}></div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
          
          </>
          )}
          </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="fixed top-1/2 left-1/2 z-[3000] -translate-x-1/2 -translate-y-1/2 transform animate-pulse rounded-lg bg-rgpd-blue px-6 py-3 text-sm font-semibold text-white shadow-xl"
        >
          {notification.message}
        </div>
      ))}

      {/* Newsletter Theme Selection Modal */}
      {showThemeModal && (
        <div 
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
          onClick={closeThemeModal}
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div 
            className="max-h-[80vh] w-[90%] max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Newsletter {currentNewsletterEvent} - Choisissez le thème
              </h3>
              <button 
                className="text-xl font-bold text-gray-400 transition-colors hover:text-gray-600"
                onClick={closeThemeModal}
              >
                ×
              </button>
            </div>
            
            <p className="mb-6 text-gray-600">
              Sélectionnez le thème principal pour cette newsletter :
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <button 
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-blue-200 bg-blue-50 p-6 transition-all duration-300 hover:border-blue-400 hover:bg-blue-100 hover:shadow-lg"
                onClick={() => handleThemeSelection('Vente')}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500 text-2xl text-white">
                  <span role="img" aria-label="briefcase">💼</span>
                </div>
                <div className="text-sm font-medium text-gray-800">Vente</div>
              </button>
              
              <button 
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-green-200 bg-green-50 p-6 transition-all duration-300 hover:border-green-400 hover:bg-green-100 hover:shadow-lg"
                onClick={() => handleThemeSelection('DPE')}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500 text-2xl text-white">
                  <span role="img" aria-label="chart">📊</span>
                </div>
                <div className="text-sm font-medium text-gray-800">DPE</div>
              </button>
              
              <button 
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-orange-200 bg-orange-50 p-6 transition-all duration-300 hover:border-orange-400 hover:bg-orange-100 hover:shadow-lg"
                onClick={() => handleThemeSelection('Travaux')}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-2xl text-white">
                  <span role="img" aria-label="Wrench">🔧</span>
                </div>
                <div className="text-sm font-medium text-gray-800">Travaux</div>
              </button>
              
              <button 
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-purple-200 bg-purple-50 p-6 transition-all duration-300 hover:border-purple-400 hover:bg-purple-100 hover:shadow-lg"
                onClick={() => handleThemeSelection('Vivre ensemble')}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500 text-2xl text-white">
                  <span role="img" aria-label="Community">🏘️</span>
                </div>
                <div className="text-sm font-medium text-gray-800">Vivre ensemble</div>
              </button>
              
              <button 
                className="flex flex-col items-center gap-3 rounded-lg border-2 border-teal-200 bg-teal-50 p-6 transition-all duration-300 hover:border-teal-400 hover:bg-teal-100 hover:shadow-lg"
                onClick={() => handleThemeSelection('Location')}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-500 text-2xl text-white">
                  <span role="img" aria-label="Key">🔑</span>
                </div>
                <div className="text-sm font-medium text-gray-800">Location</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Content Info Modal */}
      {showNewsletterInfoModal && selectedNewsletterTheme && (
        <div 
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
          onClick={closeNewsletterInfoModal}
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div 
            className="max-h-[80vh] w-[90%] max-w-3xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between border-b border-gray-200 pb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                <span dangerouslySetInnerHTML={{ __html: newsletterThemeData[selectedNewsletterTheme as keyof typeof newsletterThemeData]?.emoji || '' }} /> {newsletterThemeData[selectedNewsletterTheme as keyof typeof newsletterThemeData]?.title}
              </h3>
              <button 
                className="text-xl font-bold text-gray-400 transition-colors hover:text-gray-600"
                onClick={closeNewsletterInfoModal}
              >
                ×
              </button>
            </div>
            
            {/* Objective Section */}
            <div className="mb-6 rounded-r-lg border-l-4 border-green-500 bg-green-50 p-4">
              <div className="mb-2 flex items-center">
                <span className="mr-2 text-green-600" role="img" aria-label="Target">🎯</span>
                <h4 className="font-semibold text-green-800">Objectif :</h4>
              </div>
              <p className="text-green-700">
                {newsletterThemeData[selectedNewsletterTheme as keyof typeof newsletterThemeData]?.objective}
              </p>
            </div>

            {/* Content Section */}
            <div className="mb-6">
              <h4 className="mb-4 flex items-center font-semibold text-gray-800">
                <span className="mr-2" role="img" aria-label="Document">📄</span>
                Contenu de cette newsletter :
              </h4>
              <ul className="space-y-2">
                {newsletterThemeData[selectedNewsletterTheme as keyof typeof newsletterThemeData]?.content.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mt-2 mr-3 h-2 w-2 shrink-0 rounded-full bg-rgpd-blue"></span>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Commercial Interest Section */}
            <div className="rounded-r-lg border-l-4 border-blue-500 bg-blue-50 p-4">
              <div className="mb-2 flex items-center">
                <span className="mr-2 text-blue-600" role="img" aria-label="Chart">📈</span>
                <h4 className="font-semibold text-blue-800">Intérêt commercial :</h4>
              </div>
              <p className="text-blue-700">
                {newsletterThemeData[selectedNewsletterTheme as keyof typeof newsletterThemeData]?.interest}
              </p>
            </div>

            {/* Action Button */}
            <div className="mt-8 text-center">
              <button
                onClick={closeNewsletterInfoModal}
                className="rounded-lg bg-rgpd-blue px-8 py-3 font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-rgpd-blue-dark hover:shadow-lg focus:ring-2 focus:ring-rgpd-blue focus:ring-offset-2 focus:outline-none"
              >
                Compris !
              </button>
            </div>
          </div>
        </div>
      )}

      <NewsletterPreviewPopup
        isOpen={showNewsletterPreview}
        onClose={closeNewsletterPreview}
        newsletterType={previewNewsletterType}
        themeLabel={previewThemeLabel}
        jwt={jwt}
        company={company ?? null}
        onOpenNewsletter={handleOpenNewsletterPreview}
        onOpenList={handleOpenNewsletterList}
      />

      {/* Start Month Change Confirmation Modal */}
      {showStartMonthDialog && pendingStartMonth !== null && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
          onClick={closeStartMonthDialog}
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div
            className="max-h-[85vh] w-[92%] max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-2xl sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between border-b border-gray-200 pb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Changer le mois de lancement
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Vous passez de <strong>{MOIS_NOMS[startMonth - 1]}</strong> à <strong>{MOIS_NOMS[pendingStartMonth - 1]}</strong>. Comment souhaitez-vous gérer les dates exactes déjà saisies ?
                </p>
              </div>
              <button
                type="button"
                className="text-xl font-bold text-gray-400 transition-colors hover:text-gray-600"
                onClick={closeStartMonthDialog}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                className="flex h-full flex-col items-start gap-2 rounded-lg border-2 border-blue-200 bg-blue-50 p-5 text-left transition-all duration-300 hover:border-blue-400 hover:bg-blue-100 hover:shadow-lg focus:ring-2 focus:ring-blue-300 focus:outline-none"
                onClick={() => applyKeepDatesForStartMonth(pendingStartMonth)}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-lg text-white">
                  <span role="img" aria-label="calendar pin">📌</span>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">
                  Conserver mes dates exactes
                </h4>
                <p className="text-xs leading-relaxed text-gray-600">
                  Les événements seront déplacés sur le calendrier pour rester sur les dates exactes que vous avez saisies.
                </p>
              </button>

              <button
                type="button"
                className="flex h-full flex-col items-start gap-2 rounded-lg border-2 border-green-200 bg-green-50 p-5 text-left transition-all duration-300 hover:border-green-400 hover:bg-green-100 hover:shadow-lg focus:ring-2 focus:ring-green-300 focus:outline-none"
                onClick={() => applyClosestTuesdayForStartMonth(pendingStartMonth)}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-lg text-white">
                  <span role="img" aria-label="reorder">🗓️</span>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">
                  Conserver l'organisation
                </h4>
                <p className="text-xs leading-relaxed text-gray-600">
                  L'ordre des événements est préservé. Les dates exactes sont remplacées par le mardi le plus proche dans la nouvelle période.
                </p>
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-gray-300 focus:outline-none"
                onClick={closeStartMonthDialog}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PlanningModule; 